import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  applyPalette,
  applyTheme,
  detectSystemThemePreference,
} from '@/utils/utils';

/**
 * Zustand store for managing and persisting the theme (dark or light).
 *
 * This store uses Zustand's `persist` middleware to save the theme preference
 * in localStorage and `devtools` middleware for easy debugging during development.
 */

const themeColor = Object.freeze({
  light: '28, 153, 147',
  dark: '23, 36, 42',
});

export const useThemeStore = create(
  devtools(
    persist(
      (set, get) => ({
        theme: detectSystemThemePreference() || 'dark',
        palette:
          detectSystemThemePreference() === 'dark'
            ? themeColor.dark
            : themeColor.light,
        setTheme: (newTheme) => {
          const defaultPalette =
            newTheme === 'dark' ? themeColor.dark : themeColor.light;

          set({ theme: newTheme, palette: defaultPalette });

          applyTheme(newTheme);
          applyPalette(defaultPalette);
        },
        setPalette: (palette) => {
          set({ palette });
          applyPalette(palette);
        },
        initializeTheme: () => {
          const { theme, palette } = get();
          applyTheme(theme);
          applyPalette(
            palette || (theme === 'dark' ? themeColor.dark : themeColor.light)
          );
        },
      }),
      {
        name: 'theme-storage',
      }
    ),
    {
      name: 'ThemeStore',
    }
  )
);
